import { z } from "zod";
import { gunSemasi } from "./an-semasi";
import { GEREKCE_AGIR_EN_AZ, gerekceSemasi } from "./gerekce";

/**
 * MENTÖRÜM YANIMDA — API sözleşmesi (F-MNT-01…20, şartname §4.28; matris §5.19; TASLAK 01.11.2026, paket C-03)
 *
 * Çocuğun psikolojik/duygusal kaydı (KVKK m.6). Sunucu, panel ve öğrenci uygulaması sınırları BURADAN alır; ikinci
 * bir yerde yazılan sayı sessizce ayrışırdı (`golgeleme.test.ts`).
 *
 * Şartnamenin verdiği sınırlar: sabit duygu listesi (F-MNT-10), konu başına ≤2 video ve toplam ≤10 ses (F-MNT-06),
 * risk duygu kuralı 7 gün / ≥4 olumsuz (F-MNT-14, K-22), koçsuz erişim 1 ay (F-MNT-03), imzalı bağlantı 10 dk (K-12).
 * Metin UZUNLUKLARI şartnamede yok — aşağıdaki sayılar yazım hatasını yakalayan üst sınırlardır (C-03 varsayımı),
 * gerçek içeriği kısıtlaması beklenmez; müşteri içeriği gelince gözden geçirilir.
 */

// ---------------------------------------------------------------------------------------------
// F-MNT-10 — duygu listesi SABİT (M11 teyidi): ekleme yok, "diğer" yok
// ---------------------------------------------------------------------------------------------

/** Veritabanı enum'u `MentorumMood` ile birebir (sıra formdaki sıra). */
export const MENTORUM_DUYGULARI = [
  "TIRED",
  "EXCITED",
  "HAPPY",
  "UNHAPPY",
  "ANXIOUS",
  "OVERWHELMED",
  "ANGRY",
  "HOPEFUL",
] as const;
export type MentorumDuygu = (typeof MENTORUM_DUYGULARI)[number];

/** Ekranda görünen karşılık — müşteri form listesi (M11). */
export const MENTORUM_DUYGU_ETIKETI: Readonly<Record<MentorumDuygu, string>> = {
  TIRED: "Yorgun",
  EXCITED: "Heyecanlı",
  HAPPY: "Mutlu",
  UNHAPPY: "Mutsuz",
  ANXIOUS: "Kaygılı",
  OVERWHELMED: "Bunalmış",
  ANGRY: "Öfkeli",
  HOPEFUL: "Umutlu",
};

/**
 * F-MNT-14 / K-22 — duygu kuralında OLUMSUZ sayılanlar: kaygılı, mutsuz, bunalmış (Emre 01.11.2026).
 * "Umutsuz" ve yoğunluk derecesi sabit listede olmadığı için kurala girmez; psikolog listesi gelince gözden geçirilir.
 */
export const RISK_OLUMSUZ_DUYGULAR: readonly MentorumDuygu[] = [
  "ANXIOUS",
  "UNHAPPY",
  "OVERWHELMED",
];
/** F-MNT-14 — son 7 gün içinde… */
export const RISK_DUYGU_PENCERE_GUN = 7;
/** …4 ya da daha fazla olumsuz kayıt uyarı doğurur (eşik dahil). */
export const RISK_DUYGU_ESIK = 4;

export const duyguKaydiSemasi = z
  .object({
    mood: z.enum(MENTORUM_DUYGULARI, {
      errorMap: () => ({ message: "Duygu listeden seçilmeli." }),
    }),
  })
  .strict();
export type DuyguKaydiGirdisi = z.infer<typeof duyguKaydiSemasi>;

// ---------------------------------------------------------------------------------------------
// F-MNT-06/07 — içerik sınırları
// ---------------------------------------------------------------------------------------------

/** F-MNT-06 — konu başına en çok 2 video (sunucu 409). */
export const KONU_BASINA_VIDEO_EN_COK = 2;
/** F-MNT-06 — toplam en çok 10 ses kaydı (sunucu 409). */
export const SES_KAYDI_TOPLAM_EN_COK = 10;
/** F-MNT-07 / K-12 — imzalı medya bağlantısının ömrü (saniye). TEK sabit; bağlantı loga yazılmaz. */
export const IMZALI_BAGLANTI_OMRU_SN = 600;
/** F-MNT-03 — koçsuz öğrenci erişimi açılıştan itibaren 1 ay (uzatma yönetimle). */
export const KOCSUZ_ERISIM_AY = 1;

export const MENTORUM_BASLIK_EN_COK = 160;
export const MENTORUM_ACIKLAMA_EN_COK = 2000;
export const MENTORUM_HAZIRLAYAN_EN_COK = 120;
export const ETKINLIK_SORU_EN_COK = 1000;
export const ETKINLIK_SECENEK_METNI_EN_COK = 300;
/** Çok seçenekli soru en az 2 seçenek ister; 12 bir yazım hatasını yakalar. */
export const ETKINLIK_SECENEK_EN_AZ = 2;
export const ETKINLIK_SECENEK_EN_COK = 12;
/** F-MNT-09 — yazı alanı. Uzun olabilir (çocuk içini döküyor); üst sınır yalnız kötüye kullanım için. */
export const ETKINLIK_YANIT_METNI_EN_COK = 5000;
export const RISK_TERIMI_EN_AZ = 2;
export const RISK_TERIMI_EN_COK = 100;
export const PAKET_SURE_GUN_EN_COK = 3650;

export const MENTORUM_MEDYA_TURLERI = ["VIDEO", "AUDIO"] as const;
export type MentorumMedyaTuru = (typeof MENTORUM_MEDYA_TURLERI)[number];

const kimlik = z.string().trim().min(1, "Kimlik boş olamaz.").max(64, "Kimlik çok uzun.");

function metin(alan: string, enCok: number) {
  return z
    .string()
    .trim()
    .min(1, `${alan} boş olamaz.`)
    .max(enCok, `${alan} en fazla ${enCok} karakter olabilir.`);
}

function istegeBagliMetin(alan: string, enCok: number) {
  return z
    .string()
    .trim()
    .max(enCok, `${alan} en fazla ${enCok} karakter olabilir.`)
    .nullable()
    .optional()
    .transform((s) => (s === undefined || s === null || s.length === 0 ? null : s));
}

/** F-MNT-06 — konu; hazırlayanın adı ve unvanı yönetimce girilir (kodda yazılmaz). */
export const mentorumKonuSemasi = z
  .object({
    title: metin("Konu başlığı", MENTORUM_BASLIK_EN_COK),
    description: istegeBagliMetin("Açıklama", MENTORUM_ACIKLAMA_EN_COK),
    authorName: metin("Hazırlayanın adı", MENTORUM_HAZIRLAYAN_EN_COK),
    authorTitle: metin("Hazırlayanın unvanı", MENTORUM_HAZIRLAYAN_EN_COK),
    sortOrder: z.number().int("Sıra tam sayı olmalı.").min(0, "Sıra negatif olamaz.").default(0),
  })
  .strict();
export type MentorumKonuGirdisi = z.infer<typeof mentorumKonuSemasi>;

/** F-MNT-06/07 — medya kaydı. Nesne yüklemesi ayrı uçta; burada yalnız künye. */
export const mentorumMedyaSemasi = z
  .object({
    topicId: kimlik,
    kind: z.enum(MENTORUM_MEDYA_TURLERI, {
      errorMap: () => ({ message: "Medya türü video ya da ses olmalı." }),
    }),
    title: metin("Medya başlığı", MENTORUM_BASLIK_EN_COK),
    durationSeconds: z
      .number()
      .int("Süre tam sayı olmalı.")
      .positive("Süre sıfırdan büyük olmalı.")
      .max(6 * 60 * 60, "Süre çok uzun.")
      .nullable()
      .optional(),
    sortOrder: z.number().int("Sıra tam sayı olmalı.").min(0, "Sıra negatif olamaz.").default(0),
  })
  .strict();
export type MentorumMedyaGirdisi = z.infer<typeof mentorumMedyaSemasi>;

/**
 * F-MNT-09 — etkinlik. Seçeneklerin "riskli" işareti BURADA YOK: o `mentorum.risk.yonet` izniyle ayrı uçta
 * (`riskliSecenekSemasi`). İçerik yöneticisi işareti göremez/değiştiremez olmasın diye değil — tek yerde yazılsın.
 */
export const mentorumEtkinlikSemasi = z
  .object({
    topicId: kimlik,
    prompt: metin("Soru metni", ETKINLIK_SORU_EN_COK),
    allowsText: z.boolean().default(false),
    textPrompt: istegeBagliMetin("Yazı alanı yönergesi", ETKINLIK_SORU_EN_COK),
    options: z
      .array(z.object({ text: metin("Seçenek", ETKINLIK_SECENEK_METNI_EN_COK) }).strict())
      .min(ETKINLIK_SECENEK_EN_AZ, `En az ${ETKINLIK_SECENEK_EN_AZ} seçenek gerekli.`)
      .max(ETKINLIK_SECENEK_EN_COK, `En fazla ${ETKINLIK_SECENEK_EN_COK} seçenek olabilir.`),
    sortOrder: z.number().int("Sıra tam sayı olmalı.").min(0, "Sıra negatif olamaz.").default(0),
  })
  .strict()
  .refine((e) => e.textPrompt === null || e.allowsText, {
    path: ["textPrompt"],
    message: "Yazı alanı kapalıyken yönerge yazılamaz.",
  });
export type MentorumEtkinlikGirdisi = z.infer<typeof mentorumEtkinlikSemasi>;

/**
 * F-MNT-09/11/12 — etkinlik yanıtı. `gonder: false` taslaktır (saklanır, defterde görünmez); `true` gönderimdir ve
 * gönderilen yanıt bir daha değişmez (veritabanı tetikleyicisi). Seçenek kimlikleri tekil olmalı.
 */
export const etkinlikYanitiSemasi = z
  .object({
    selectedOptionIds: z
      .array(kimlik)
      .max(ETKINLIK_SECENEK_EN_COK, "Çok fazla seçenek işaretlendi.")
      .refine((d) => new Set(d).size === d.length, "Aynı seçenek iki kez işaretlenemez."),
    textAnswer: istegeBagliMetin("Yanıt", ETKINLIK_YANIT_METNI_EN_COK),
    gonder: z.boolean(),
  })
  .strict()
  .refine((y) => !y.gonder || y.selectedOptionIds.length > 0 || y.textAnswer !== null, {
    path: ["selectedOptionIds"],
    message: "Göndermeden önce en az bir seçenek işaretleyin ya da yazı alanını doldurun.",
  });
export type EtkinlikYanitiGirdisi = z.infer<typeof etkinlikYanitiSemasi>;

/** F-MNT-09 / C03-08 — gönderim kopyası eski soru ve seçenek metinlerini kaybetmeden okunabilmeli. */
export const etkinlikYanitiKopyasiSemasi = z
  .object({
    prompt: metin("Soru metni", ETKINLIK_SORU_EN_COK),
    textPrompt: metin("Yazı alanı yönergesi", ETKINLIK_SORU_EN_COK).nullable(),
    options: z
      .array(
        z
          .object({
            id: kimlik,
            text: metin("Seçenek", ETKINLIK_SECENEK_METNI_EN_COK),
            selected: z.boolean(),
          })
          .strict(),
      )
      .min(ETKINLIK_SECENEK_EN_AZ)
      .max(ETKINLIK_SECENEK_EN_COK),
  })
  .strict();
export type EtkinlikYanitiKopyasi = z.infer<typeof etkinlikYanitiKopyasiSemasi>;

// ---------------------------------------------------------------------------------------------
// F-MNT-14 — risk listesi ve riskli seçenek (izin `mentorum.risk.yonet`)
// ---------------------------------------------------------------------------------------------

export const riskTerimiSemasi = z
  .object({
    term: z
      .string()
      .trim()
      .min(RISK_TERIMI_EN_AZ, `Kelime en az ${RISK_TERIMI_EN_AZ} karakter olmalı.`)
      .max(RISK_TERIMI_EN_COK, `Kelime en fazla ${RISK_TERIMI_EN_COK} karakter olabilir.`),
  })
  .strict();
export type RiskTerimiGirdisi = z.infer<typeof riskTerimiSemasi>;

export const riskliSecenekSemasi = z.object({ optionId: kimlik, isRisky: z.boolean() }).strict();
export type RiskliSecenekGirdisi = z.infer<typeof riskliSecenekSemasi>;

// ---------------------------------------------------------------------------------------------
// F-MNT-02/03 — paket ve erişim hakkı (izin `mentorum.erisim.yonet`)
// ---------------------------------------------------------------------------------------------

export const MENTORUM_ERISIM_TURLERI = ["COACHING", "STANDALONE"] as const;
export type MentorumErisimTuru = (typeof MENTORUM_ERISIM_TURLERI)[number];

export const mentorumPaketSemasi = z
  .object({
    name: metin("Paket adı", MENTORUM_BASLIK_EN_COK),
    description: istegeBagliMetin("Açıklama", MENTORUM_ACIKLAMA_EN_COK),
    /** Boş = koçluk üyeliği sürdükçe */
    durationDays: z
      .number()
      .int("Süre tam gün olmalı.")
      .positive("Süre sıfırdan büyük olmalı.")
      .max(PAKET_SURE_GUN_EN_COK, "Süre çok uzun.")
      .nullable()
      .optional(),
  })
  .strict();
export type MentorumPaketGirdisi = z.infer<typeof mentorumPaketSemasi>;

/**
 * Erişim verme. STANDALONE (koçsuz) için bitiş sunucuda açılıştan 1 ay sonra hesaplanır (`KOCSUZ_ERISIM_AY`);
 * istemci bitiş göndermez — gönderebilseydi "1 ay" kuralı istemciye kalırdı.
 */
export const mentorumErisimVerSemasi = z
  .object({
    studentId: kimlik,
    packageId: kimlik.nullable().optional(),
    kind: z.enum(MENTORUM_ERISIM_TURLERI, {
      errorMap: () => ({ message: "Erişim türü geçersiz." }),
    }),
  })
  .strict();
export type MentorumErisimVerGirdisi = z.infer<typeof mentorumErisimVerSemasi>;

/** Sonlandırma gerekçesi denetim kaydının üst düzey `reason` alanına yazılır (yüke değil). */
export const mentorumErisimSonlandirSemasi = z.object({ reason: gerekceSemasi() }).strict();
export type MentorumErisimSonlandirGirdisi = z.infer<typeof mentorumErisimSonlandirSemasi>;

// ---------------------------------------------------------------------------------------------
// F-MNT-16 — resmi başvuru kopyası (yalnız SUPER_ADMIN, sudo)
// ---------------------------------------------------------------------------------------------

/** Gerekçe ağır işlem eşiğinde: resmi başvurunun dayanağı sonradan sorulacak. */
export const mentorumDisaAktarSemasi = z
  .object({
    studentId: kimlik,
    reason: gerekceSemasi({
      enAz: GEREKCE_AGIR_EN_AZ,
      mesaj: `Resmi başvurunun dayanağını en az ${GEREKCE_AGIR_EN_AZ} karakterle yazın.`,
    }),
  })
  .strict();
export type MentorumDisaAktarGirdisi = z.infer<typeof mentorumDisaAktarSemasi>;

// ---------------------------------------------------------------------------------------------
// Hata kodları — kapı sırası: bayrak (404) → üyelik → rıza → kapsam (01-mimari)
// ---------------------------------------------------------------------------------------------

export const MENTORUM_HATA_KODLARI = {
  /** F-MNT-02/03 — erişim yok ya da süresi doldu */
  ERISIM_YOK: "MENTORUM_ERISIM_YOK",
  /** F-MNT-05 — öğrenci bilgilendirmesini onaylamadı */
  ONAY_BEKLIYOR: "MENTORUM_ONAYI_BEKLIYOR",
  /** F-MNT-05 — 18 altı, veli rızası yok / geri çekildi / velisi tanımsız */
  VELI_ONAYI_BEKLIYOR: "VELI_ONAYI_BEKLIYOR",
  /** F-MNT-08 — açılan konunun etkinlikleri gönderilmedi */
  KONU_TAMAMLANMADI: "KONU_TAMAMLANMADI",
} as const;

// F-MNT-14/M11 — kapanış nedeni ortak listeden seçilir; serbest metin denetime taşınamaz.
export const MENTORUM_RISK_KAPANIS_NEDENLERI = [
  "STUDENT_CONTACTED",
  "PARENT_CONTACTED",
  "REFERRED_TO_SPECIALIST",
  "FALSE_ALARM",
] as const;
export const mentorumRiskKapatSemasi = z
  .object({ handledReason: z.enum(MENTORUM_RISK_KAPANIS_NEDENLERI) })
  .strict();

// F-MNT-03 — rol ve koç istemciden alınmaz; sunucu yalnız sabit öğrenci rolünü atar.
export const kocsuzHesapSemasi = z
  .object({
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable().optional(),
    birthDate: z.string().date(),
    gradeLevel: z.enum(["G5", "G6", "G7", "G8", "G9", "G10", "G11", "G12", "GRADUATE", "OTHER"]),
    packageId: kimlik.nullable().optional(),
  })
  .strict();
export type KocsuzHesapGirdisi = z.infer<typeof kocsuzHesapSemasi>;

export const MENTORUM_RIZA_TURLERI = ["STUDENT_NOTICE", "PARENT_CONSENT"] as const;
export const mentorumRizaBelgesiSemasi = z
  .object({
    kind: z.enum(MENTORUM_RIZA_TURLERI),
    version: metin("Sürüm", 64),
    title: metin("Başlık", MENTORUM_BASLIK_EN_COK),
    body: metin("Metin", 100_000),
  })
  .strict();
export const mentorumRizaVerSemasi = z.object({ documentId: kimlik }).strict();
export const mentorumRizaGeriCekSemasi = z.object({ kind: z.enum(MENTORUM_RIZA_TURLERI) }).strict();
// F-MNT-05 — kanıt yalnız aynı istekte yüklenir; başka dosyanın kimliğiyle onay verilemez.
export const mentorumKagitRizaSemasi = z
  .object({ documentId: kimlik, veliUserId: kimlik, reason: gerekceSemasi() })
  .strict();
const rizaBelgesiYaniti = mentorumRizaBelgesiSemasi.extend({
  id: kimlik,
  publishedAt: z.string().datetime().nullable(),
  supersededAt: z.string().datetime().nullable(),
});
export const mentorumRizaBelgesiYanitiSemasi = rizaBelgesiYaniti;
export const mentorumRizaYanitiSemasi = z
  .object({
    id: kimlik,
    kind: z.enum(MENTORUM_RIZA_TURLERI),
    documentId: kimlik,
    source: z.enum(["DIGITAL", "PAPER"]),
    grantedAt: z.string().datetime(),
    withdrawnAt: z.string().datetime().nullable(),
  })
  .strict();
const mentorumTamRizaDurumuSemasi = z
  .object({
    studentId: kimlik,
    veliGerekli: z.boolean(),
    veliBagiDogrulandi: z.boolean(),
    ogrenciOnayi: z.boolean(),
    veliOnayi: z.boolean(),
    belgeler: z.array(rizaBelgesiYaniti),
  })
  .strict();

// F-MNT-05 — bekleme yanıtında onay ve belge alanları bulunamaz; eksik tam durum da kabul edilmez.
/** ME-02 — `GET /mentorum/modul-durumu`; kapalıyken uç 404 döner, bu yüzden yanıt yalnız açık hali taşır. */
export const mentorumModulDurumuSemasi = z.object({ acik: z.literal(true) }).strict();
export type MentorumModulDurumu = z.infer<typeof mentorumModulDurumuSemasi>;

export const mentorumRizaDurumuSemasi = z.union([
  z.object({ studentId: kimlik, veliBagiDogrulandi: z.literal(false) }).strict(),
  mentorumTamRizaDurumuSemasi,
]);

// F-MNT-06/14 — öğrenci sözleşmesi alanları açıkça sayar; risk işareti bu sınırı geçemez.
export const mentorumEtkinlikOkumaSemasi = z.array(
  z
    .object({
      id: kimlik,
      topicId: kimlik,
      prompt: z.string(),
      allowsText: z.boolean(),
      textPrompt: z.string().nullable(),
      sortOrder: z.number().int(),
      options: z.array(
        z.object({ id: kimlik, text: z.string(), sortOrder: z.number().int() }).strict(),
      ),
    })
    .strict(),
);
export type MentorumEtkinlikOkumaYaniti = z.infer<typeof mentorumEtkinlikOkumaSemasi>;
export type MentorumRizaDurumuYaniti = z.infer<typeof mentorumRizaDurumuSemasi>;

export type MentorumRiskKapanisNedeni = (typeof MENTORUM_RISK_KAPANIS_NEDENLERI)[number];
export type MentorumRiskKapatGirdisi = z.infer<typeof mentorumRiskKapatSemasi>;
export type MentorumRizaBelgesiGirdisi = z.infer<typeof mentorumRizaBelgesiSemasi>;
export type MentorumRizaVerGirdisi = z.infer<typeof mentorumRizaVerSemasi>;
export type MentorumRizaGeriCekGirdisi = z.infer<typeof mentorumRizaGeriCekSemasi>;
export type MentorumKagitRizaGirdisi = z.infer<typeof mentorumKagitRizaSemasi>;
export type MentorumRizaBelgesiYaniti = z.infer<typeof mentorumRizaBelgesiYanitiSemasi>;
export type MentorumRizaYaniti = z.infer<typeof mentorumRizaYanitiSemasi>;

// F-MNT-03 — rol ve geçiş anı sunucunun kararıdır; gövde bunları belirleyemez.
export const mentorumKoclugaGecisSemasi = z
  .object({
    coachId: kimlik,
    abonelik: z
      .object({
        paketId: kimlik,
        baslangic: gunSemasi,
        bitis: gunSemasi,
        indirim: z.string().min(1).max(24).nullish(),
        taksitSayisi: z.number().int().min(1).max(36),
        ilkVade: gunSemasi,
        araAy: z.number().int().min(1).max(12).optional(),
      })
      .strict()
      .refine((veri) => veri.bitis > veri.baslangic, {
        path: ["bitis"],
        message: "Bitiş başlangıçtan sonra olmalı.",
      }),
  })
  .strict();
export type MentorumKoclugaGecisGirdisi = z.infer<typeof mentorumKoclugaGecisSemasi>;
